import { useState, useEffect } from 'react';
import {
  Box,
  Checkbox,
  FormControlLabel,
  Typography,
  Collapse,
  IconButton,
  Paper,
  Divider,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  IndeterminateCheckBox as IndeterminateCheckBoxIcon,
} from '@mui/icons-material';

/**
 * PermissionTree - Componente de árbol de permisos con checkboxes jerárquicos
 * 
 * Props:
 * - permissions: objeto con estructura jerárquica de permisos
 * - selectedPermissions: array de códigos de permisos seleccionados
 * - onChange: callback cuando cambian los permisos seleccionados
 */
const PermissionTree = ({ permissions, selectedPermissions = [], onChange }) => {
  const [selected, setSelected] = useState([]);
  const [expanded, setExpanded] = useState({});

  // Sincronizar con selectedPermissions cuando cambien desde fuera
  useEffect(() => {
    setSelected(selectedPermissions);
  }, [selectedPermissions]);

  // Expandir todos los módulos por defecto
  useEffect(() => {
    if (permissions) {
      const allModules = {};
      Object.keys(permissions).forEach((key) => {
        allModules[key] = true;
      });
      setExpanded(allModules);
    }
  }, [permissions]);

  const handleToggleExpand = (moduleCodigo) => {
    setExpanded((prev) => ({
      ...prev,
      [moduleCodigo]: !prev[moduleCodigo],
    }));
  };

  const handleModuleChange = (modulePermiso, hijos) => {
    const isSelected = selected.includes(modulePermiso.codigo);
    let newSelected = [...selected];

    if (isSelected) {
      // Desmarcar módulo y todos sus hijos
      newSelected = newSelected.filter(
        (codigo) =>
          codigo !== modulePermiso.codigo &&
          !hijos.some((hijo) => hijo.codigo === codigo)
      );
    } else {
      // Marcar módulo y todos sus hijos
      newSelected.push(modulePermiso.codigo);
      hijos.forEach((hijo) => {
        if (!newSelected.includes(hijo.codigo)) {
          newSelected.push(hijo.codigo);
        }
      });
    }

    setSelected(newSelected);
    onChange(newSelected);
  };

  const handleChildChange = (moduleCodigo, childPermiso) => {
    const isSelected = selected.includes(childPermiso.codigo);
    let newSelected = [...selected];

    if (isSelected) {
      // Desmarcar hijo
      newSelected = newSelected.filter((codigo) => codigo !== childPermiso.codigo);
    } else {
      // Marcar hijo
      newSelected.push(childPermiso.codigo);
      
      // Si no tiene el módulo padre, agregarlo también
      if (!newSelected.includes(moduleCodigo)) {
        newSelected.push(moduleCodigo);
      }
    }

    setSelected(newSelected);
    onChange(newSelected);
  };

  const getModuleCheckState = (moduleCodigo, hijos) => {
    const hasModule = selected.includes(moduleCodigo);
    const selectedChildren = hijos.filter((hijo) =>
      selected.includes(hijo.codigo)
    ).length;

    if (!hasModule && selectedChildren === 0) {
      return 'unchecked';
    } else if (hasModule && selectedChildren === hijos.length) {
      return 'checked';
    } else {
      return 'indeterminate';
    }
  };

  if (!permissions) {
    return <Typography>Cargando permisos...</Typography>;
  }

  return (
    <Box>
      {Object.entries(permissions).map(([key, { permiso: modulePermiso, hijos }]) => {
        const checkState = getModuleCheckState(modulePermiso.codigo, hijos);
        const isExpanded = expanded[modulePermiso.codigo];

        return (
          <Paper
            key={modulePermiso.codigo}
            elevation={1}
            sx={{ mb: 2, overflow: 'hidden' }}
          >
            {/* Módulo Padre */}
            <Box
              sx={{
                p: 2,
                bgcolor: 'primary.main',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={checkState === 'checked'}
                    indeterminate={checkState === 'indeterminate'}
                    onChange={() => handleModuleChange(modulePermiso, hijos)}
                    icon={<CheckBoxOutlineBlankIcon sx={{ color: 'white' }} />}
                    checkedIcon={<CheckBoxIcon sx={{ color: 'white' }} />}
                    indeterminateIcon={<IndeterminateCheckBoxIcon sx={{ color: 'white' }} />}
                  />
                }
                label={
                  <Box>
                    <Typography variant="h6" fontWeight="bold">
                      {modulePermiso.nombre}
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.9 }}>
                      {modulePermiso.descripcion}
                    </Typography>
                  </Box>
                }
                sx={{ flex: 1, mr: 1 }}
              />
              <IconButton
                onClick={() => handleToggleExpand(modulePermiso.codigo)}
                sx={{ color: 'white' }}
              >
                {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>

            {/* Permisos Hijos */}
            <Collapse in={isExpanded}>
              <Box sx={{ p: 2 }}>
                {hijos.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    Sin sub-permisos
                  </Typography>
                ) : (
                  hijos.map((hijo, index) => (
                    <Box key={hijo.codigo}>
                      {index > 0 && <Divider sx={{ my: 1 }} />}
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selected.includes(hijo.codigo)}
                            onChange={() =>
                              handleChildChange(modulePermiso.codigo, hijo)
                            }
                            disabled={!selected.includes(modulePermiso.codigo)}
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body1" fontWeight="medium">
                              {hijo.nombre}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {hijo.descripcion}
                            </Typography>
                          </Box>
                        }
                        sx={{
                          width: '100%',
                          ml: 2,
                          opacity: selected.includes(modulePermiso.codigo) ? 1 : 0.5,
                        }}
                      />
                    </Box>
                  ))
                )}
              </Box>
            </Collapse>
          </Paper>
        );
      })}
    </Box>
  );
};

export default PermissionTree;


